import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { TrendingUp, People, Description, Visibility, Favorite } from '@material-ui/icons';
import { fetchCreatorTypeCategory, fetchYtCreatorTypeCategory } from 'api_handlers/competitor';
import { getBrandColor } from '../lib/utils';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
  },
  metricFilters: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  metricButton: {
    minWidth: 100,
    textTransform: 'none',
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: '8px',
    '& .MuiTableCell-root': {
      padding: theme.spacing(1.5),
      borderBottom: 'none',
      borderRadius: 4,
    },
    '& .MuiTableCell-head': {
      fontWeight: 600,
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      borderBottom: 'none',
    },
  },
  categoryCell: {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  dataCell: {
    textAlign: 'center',
    position: 'relative',
  },
  cellValue: {
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.25),
  },
  cellPercentage: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  insightBox: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  insightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  insightText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  legendBox: {
    width: 16,
    height: 16,
    borderRadius: 2,
  },
}));

const METRICS = ['Influencers', 'Posts', 'Views', 'Engagement'];

// Map metric names to icons
const METRIC_TO_ICON = {
  'Influencers': People,
  'Posts': Description,
  'Views': Visibility,
  'Engagement': Favorite,
};

// Map metric names to API sort_by values
const METRIC_TO_SORT_BY = {
  'Influencers': '-creators',
  'Posts': '-count',
  'Views': '-view_count',
  'Engagement': '-likes_count', // We'll calculate engagement from likes + comments
};

// Get brand color with shade based on value relative to max
const getCellColor = (value, maxValue, brandIndex) => {
  if (maxValue === 0 || value === 0) {
    return 'rgba(243, 244, 246, 0.3)'; // Very light grey for zero
  }
  
  const ratio = value / maxValue;
  const brandColor = getBrandColor(brandIndex);
  const brandColorHex = brandColor.replace('#', '');
  
  const r = parseInt(brandColorHex.substring(0, 2), 16);
  const g = parseInt(brandColorHex.substring(2, 4), 16);
  const b = parseInt(brandColorHex.substring(4, 6), 16);

  // Calculate opacity based on ratio
  // Higher values = higher opacity (darker), lower values = lower opacity (lighter)
  const minOpacity = 0.15;
  const maxOpacity = 0.85;
  const opacity = minOpacity + (ratio * (maxOpacity - minOpacity));
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export default function CategoryBreakdownTable({ 
  selectedBrands = [],
  competitorBrands = [], // Array of brand objects with ig_handle and yt_handle
  start_date = null,
  end_date = null,
  onMetricChange: onMetricChangeCallback,
  renderButtonsOnly = false,
  selectedMetric: controlledMetric,
  onMetricChange: onControlledMetricChange
}) {
  const classes = useStyles();
  const [internalMetric, setInternalMetric] = useState('Posts');
  const selectedMetric = controlledMetric !== undefined ? controlledMetric : internalMetric;
  
  const handleMetricChange = (metric) => {
    if (onControlledMetricChange) {
      onControlledMetricChange(metric);
    } else {
      setInternalMetric(metric);
    }
    if (onMetricChangeCallback) {
      onMetricChangeCallback(metric);
    }
  };
  const [loading, setLoading] = useState(true);
  const [categoryData, setCategoryData] = useState({});

  // Fetch category data for all brands
  useEffect(() => {
    const fetchCategoryData = async () => {
      if (selectedBrands.length === 0 || competitorBrands.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const data = {};

      try {
        // Fetch data for each brand
        for (const brandTitle of selectedBrands) {
          const brandInfo = competitorBrands.find(b => b.title === brandTitle);
          if (!brandInfo) continue;

          data[brandTitle] = {};

          // Fetch Instagram data
          if (brandInfo.ig_handle) {
            try {
              const sortBy = METRIC_TO_SORT_BY[selectedMetric] || '-count';
              const resp = await fetchCreatorTypeCategory(
                brandInfo.ig_handle,
                start_date,
                end_date,
                sortBy,
                '',
                ''
              );
              
              if (resp && resp.category_type) {
                resp.category_type.forEach((item) => {
                  const categoryName = item.category_name || 'Other';
                  if (!data[brandTitle][categoryName]) {
                    data[brandTitle][categoryName] = {
                      creators: 0,
                      count: 0,
                      view_count: 0,
                      likes_count: 0,
                      comments_count: 0,
                    };
                  }
                  data[brandTitle][categoryName].creators += item.creators || 0;
                  data[brandTitle][categoryName].count += item.count || 0;
                  data[brandTitle][categoryName].view_count += item.view_count || 0;
                  data[brandTitle][categoryName].likes_count += item.likes_count || 0;
                  data[brandTitle][categoryName].comments_count += item.comments_count || 0;
                });
              }
            } catch (error) {
              console.error(`Error fetching Instagram category data for ${brandTitle}:`, error);
            }
          }

          // Fetch YouTube data
          if (brandInfo.yt_handle && !brandInfo.yt_handle.toLowerCase().includes('test')) {
            try {
              const sortBy = METRIC_TO_SORT_BY[selectedMetric] || '-count';
              const resp = await fetchYtCreatorTypeCategory(
                brandInfo.yt_handle,
                start_date,
                end_date,
                sortBy,
                '',
                ''
              );
              
              if (resp && resp.category_type) {
                resp.category_type.forEach((item) => {
                  const categoryName = item.category_name || 'Other';
                  if (!data[brandTitle][categoryName]) {
                    data[brandTitle][categoryName] = {
                      creators: 0,
                      count: 0,
                      view_count: 0,
                      likes_count: 0,
                      comments_count: 0,
                    };
                  }
                  data[brandTitle][categoryName].creators += item.creators || 0;
                  data[brandTitle][categoryName].count += item.count || 0;
                  data[brandTitle][categoryName].view_count += item.view_count || 0;
                  data[brandTitle][categoryName].likes_count += item.likes_count || 0;
                  data[brandTitle][categoryName].comments_count += item.comments_count || 0;
                });
              }
            } catch (error) {
              console.error(`Error fetching YouTube category data for ${brandTitle}:`, error);
            }
          }
        }

        setCategoryData(data);
      } catch (error) {
        console.error('Error fetching category data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryData();
  }, [selectedBrands, competitorBrands, start_date, end_date, selectedMetric]);

  // Get all unique categories from the data
  const categories = useMemo(() => {
    const categorySet = new Set();
    Object.values(categoryData).forEach((brandData) => {
      Object.keys(brandData).forEach((category) => {
        categorySet.add(category);
      });
    });
    return Array.from(categorySet).sort();
  }, [categoryData]);

  // Transform API data to table format
  const tableData = useMemo(() => {
    if (categories.length === 0 || selectedBrands.length === 0) {
      return [];
    }

    return categories.map((category) => {
      const row = { category };
      let categoryTotal = 0;
      
      // Calculate total for this category across all brands based on selected metric
      selectedBrands.forEach((brand) => {
        const brandCategoryData = categoryData[brand]?.[category];
        if (!brandCategoryData) return;
        
        let value = 0;
        if (selectedMetric === 'Influencers') {
          value = brandCategoryData.creators || 0;
        } else if (selectedMetric === 'Posts') {
          value = brandCategoryData.count || 0;
        } else if (selectedMetric === 'Views') {
          value = brandCategoryData.view_count || 0;
        } else if (selectedMetric === 'Engagement') {
          value = (brandCategoryData.likes_count || 0) + (brandCategoryData.comments_count || 0);
        }
        categoryTotal += value;
      });
      
      // Store category total in the row
      row.categoryTotal = categoryTotal;
      
      // Add brand values and percentages
      selectedBrands.forEach((brand) => {
        const brandCategoryData = categoryData[brand]?.[category];
        let value = 0;
        
        if (brandCategoryData) {
          if (selectedMetric === 'Influencers') {
            value = brandCategoryData.creators || 0;
          } else if (selectedMetric === 'Posts') {
            value = brandCategoryData.count || 0;
          } else if (selectedMetric === 'Views') {
            value = brandCategoryData.view_count || 0;
          } else if (selectedMetric === 'Engagement') {
            value = (brandCategoryData.likes_count || 0) + (brandCategoryData.comments_count || 0);
          }
        }
        
        row[brand] = {
          value,
          percentage: categoryTotal > 0 ? ((value / categoryTotal) * 100).toFixed(1) : '0.0',
        };
      });
      
      return row;
    });
  }, [categoryData, categories, selectedBrands, selectedMetric]);

  // Find max value for color scaling (including category totals)
  const maxValue = useMemo(() => {
    let max = 0;
    tableData.forEach((row) => {
      selectedBrands.forEach((brand) => {
        if (row[brand]?.value > max) {
          max = row[brand].value;
        }
      });
      // Also consider category totals for max value
      if (row.categoryTotal > max) {
        max = row.categoryTotal;
      }
    });
    return max;
  }, [tableData, selectedBrands]);


  // Generate insight text
  const generateInsight = () => {
    if (tableData.length === 0) return 'No data available.';
    
    // Find category with highest value for each brand
    const brandLeads = {};
    selectedBrands.forEach((brand) => {
      let maxValue = 0;
      let leadingCategory = '';
      tableData.forEach((row) => {
        if (row[brand]?.value > maxValue) {
          maxValue = row[brand].value;
          leadingCategory = row.category;
        }
      });
      if (leadingCategory && maxValue > 0) {
        brandLeads[brand] = { category: leadingCategory, value: maxValue };
      }
    });
    
    if (Object.keys(brandLeads).length === 0) {
      return 'Data shows varied distribution across categories and brands.';
    }
    
    const insights = Object.entries(brandLeads)
      .map(([brand, data]) => `${brand} leads in ${data.category} (${formatValue(data.value)} ${selectedMetric.toLowerCase()})`)
      .join('. ');
    
    return insights.length > 0 
      ? `${insights}, suggesting distinct positioning strategies across categories.`
      : 'Data shows varied distribution across categories and brands.';
  };

  const formatValue = (value) => {
    if (selectedMetric === 'Views' || selectedMetric === 'Engagement') {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`;
      }
      if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
      }
    }
    return value.toString();
  };

  if (loading) {
    return (
      <Box className={classes.container} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // Render buttons only if requested
  if (renderButtonsOnly) {
    return (
      <Box className={classes.metricFilters} style={{ marginBottom: 0 }}>
        {METRICS.map((metric) => {
          const Icon = METRIC_TO_ICON[metric];
          return (
            <Button
              key={metric}
              variant={selectedMetric === metric ? 'contained' : ''}
              size="small"
              onClick={() => handleMetricChange(metric)}
              className={classes.metricButton}
              startIcon={<Icon />}
            >
              {metric}
            </Button>
          );
        })}
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Metric Filters */}
      {/* <Box className={classes.metricFilters}>
        {METRICS.map((metric) => {
          const Icon = METRIC_TO_ICON[metric];
          return (
            <Button
              key={metric}
              variant={selectedMetric === metric ? 'contained' : ''}
              size="small"
              onClick={() => handleMetricChange(metric)}
              className={classes.metricButton}
              startIcon={<Icon />}
            >
              {metric}
            </Button>
          );
        })}
      </Box> */}

      {/* Table */}
      {tableData.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="textSecondary">No genre data available</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} style={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.categoryCell}>Genre</TableCell>
              {selectedBrands.map((brand) => (
                <TableCell key={brand} align="center" className={classes.categoryCell}>
                  {brand}
                </TableCell>
              ))}
              <TableCell align="center" className={classes.categoryCell} style={{ fontWeight: 600 }}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((row) => {
              const categoryTotal = row.categoryTotal || 0;
              // Use a neutral color for the total column (average of brand colors or grey)
              const totalBackgroundColor = getCellColor(categoryTotal, maxValue, selectedBrands.length);
              
              return (
                <TableRow key={row.category}>
                  <TableCell className={classes.categoryCell}>{row.category}</TableCell>
                  {selectedBrands.map((brand, brandIndex) => {
                    const cellData = row[brand] || { value: 0, percentage: '0.0' };
                    const backgroundColor = getCellColor(cellData.value, maxValue, brandIndex);
                    
                    return (
                      <TableCell
                        key={brand}
                        className={classes.dataCell}
                        style={{ backgroundColor }}
                      >
                        <Typography className={classes.cellValue}>
                          {formatValue(cellData.value)}
                        </Typography>
                        <Typography className={classes.cellPercentage}>
                          ({cellData.percentage}%)
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell
                    className={classes.dataCell}
                    style={{ 
                      backgroundColor: totalBackgroundColor,
                      fontWeight: 600
                    }}
                  >
                    <Typography className={classes.cellValue}>
                      {formatValue(categoryTotal)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Key Insight */}
      <Box className={classes.insightBox}>
        <Box className={classes.insightHeader}>
          <TrendingUp style={{ color: '#10b981', fontSize: 16 }} />
          <Typography variant="subtitle2" style={{ fontWeight: 500 }}>
            Key Insight
          </Typography>
        </Box>
        <Typography className={classes.insightText}>
          {generateInsight()}
        </Typography>
        <Box className={classes.legend}>
          <Typography variant="caption">
            Color intensity indicates relative performance within each category
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

